import React, { useState, useRef } from 'react';
import { X, AlertCircle, Loader2, Sparkles, ClipboardPaste, FileUp } from 'lucide-react';
import { parseResumeText } from '../utils.js';
import { generateAIContent, getPrompts } from '../aiService';
import { extractTextFromPdf } from '../pdfImport.js';
import BackgroundLayout from './ui/BackgroundLayout';
import { useTheme } from '../contexts/ThemeContext';

const PasteMode = ({ setStep, setResumeData, showNotification, apiConfig }) => {
    const { isDarkMode } = useTheme();
    const [pasteText, setPasteText] = useState("");
    const [isParsing, setIsParsing] = useState(false);
    const [isExtractingPdf, setIsExtractingPdf] = useState(false);
    const pdfInputRef = useRef(null);

    const handlePdfUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsExtractingPdf(true);
        try {
            const text = await extractTextFromPdf(file);
            setPasteText(text);
            showNotification(`Extracted ${text.length} chars from PDF. Click "Parse Content" to continue.`);
        } catch (err) {
            console.error('PDF extract error:', err);
            showNotification(err.message || 'Failed to extract PDF text.');
        } finally {
            setIsExtractingPdf(false);
            e.target.value = '';
        }
    };

    const handleTextPasteParse = async () => {
        if (!pasteText.trim()) return;

        // Smart Parsing with AI if API Key exists
        const hasApiKey = apiConfig && (apiConfig.gemini || apiConfig.openai || apiConfig.anthropic || apiConfig.groq || apiConfig.deepseek);
        if (hasApiKey) {
            setIsParsing(true);
            try {
                const prompt = getPrompts('parse-resume', { text: pasteText });
                const response = await generateAIContent(apiConfig, prompt);

                // Clean response of markdown
                const jsonStr = response.replace(/```json/g, '').replace(/```/g, '').trim();
                const parsedData = JSON.parse(jsonStr);

                // Ensure IDs are unique numbers (AI might give static numbers)
                if (parsedData.experience) {
                    parsedData.experience = parsedData.experience.map((e, i) => ({ ...e, id: Date.now() + i }));
                }
                if (parsedData.education) {
                    parsedData.education = parsedData.education.map((e, i) => ({ ...e, id: Date.now() + i + 100 }));
                }

                // Merge with default structure to ensure missing fields don't break things
                setResumeData(prev => ({
                    ...prev,
                    ...parsedData,
                    personal: { ...prev.personal, ...parsedData.personal },
                    sectionOrder: prev.sectionOrder, // Keep default order
                    customSections: prev.customSections
                }));

                setStep('editor');
                showNotification("Context Smartly Parsed!");
                return;

            } catch (error) {
                console.error("AI Parse Error:", error);
                showNotification("AI Parsing failed, falling back to basic parser.");
                // Fallthrough to basic parser
            } finally {
                setIsParsing(false);
            }
        }

        // Legacy/Fallback Parser
        const parsed = parseResumeText(pasteText);
        setResumeData(parsed);
        setStep('editor');
        showNotification("Text parsed successfully!");
    };

    return (
        <BackgroundLayout className="flex items-center justify-center p-6">
            <div className={`max-w-3xl w-full rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[700px] border transition-colors duration-300 ${isDarkMode
                ? 'glass-dark border-white/10'
                : 'bg-white border-white/50 shadow-blue-500/5'
                }`}>
                <div className={`p-6 flex justify-between items-center border-b transition-colors ${isDarkMode
                    ? 'bg-slate-900/50 border-white/5'
                    : 'bg-slate-50 border-slate-100'
                    }`}>
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-purple-500/10' : 'bg-purple-50'}`}>
                            <ClipboardPaste className={isDarkMode ? 'text-purple-400' : 'text-purple-600'} size={24} />
                        </div>
                        <div>
                            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Paste Resume Text</h2>
                            <p className={`${isDarkMode ? 'text-slate-400' : 'text-slate-500'} text-xs mt-0.5`}>Copy content from your PDF/Word doc and paste below.</p>
                        </div>
                    </div>
                    <button onClick={() => setStep('onboarding')} className={`transition-colors p-2 rounded-lg ${isDarkMode
                        ? 'text-slate-400 hover:text-white hover:bg-white/5'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                        }`}>
                        <X size={20} />
                    </button>
                </div>
                <div className="flex-1 p-6 flex flex-col">
                    <div className={`p-4 rounded-xl mb-4 text-sm flex items-start gap-3 border ${isDarkMode
                        ? 'bg-blue-500/10 border-blue-500/20 text-blue-200'
                        : 'bg-blue-50 border-blue-100 text-blue-800'
                        }`}>
                        <AlertCircle size={18} className={`shrink-0 mt-0.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                        <div>
                            <p className={`mb-1 ${isDarkMode ? 'text-blue-100' : 'text-blue-900'}`}><strong>Pro Tip:</strong> Paste text directly from your <strong>PDF resume</strong> or <strong>LinkedIn profile</strong> (Save to PDF &gt; Copy all) for a quick start.</p>
                            <p className={`${isDarkMode ? 'text-slate-400' : 'text-slate-600'} text-xs`}>Ensure dates and section headers (Experience, Education) are on their own lines for best parsing.</p>
                        </div>
                    </div>
                    <textarea
                        className={`flex-1 w-full border rounded-xl p-4 font-mono text-xs md:text-sm resize-none focus:ring-2 focus:ring-blue-500 outline-none leading-relaxed transition-colors ${isDarkMode
                            ? 'bg-slate-800/50 border-slate-700 text-slate-300 placeholder:text-slate-600 focus:bg-slate-800'
                            : 'bg-slate-50 border-slate-200 text-slate-700 placeholder:text-slate-400 focus:bg-white'
                            }`}
                        placeholder="Example:&#10;Alex Morgan&#10;New York, USA | +1 555-0123&#10;&#10;EXPERIENCE&#10;Tech Corp — Senior Engineer&#10;May 2023 – Present&#10;..."
                        value={pasteText}
                        onChange={(e) => setPasteText(e.target.value)}
                    />
                </div>
                <div className={`p-6 border-t flex justify-end gap-3 ${isDarkMode
                    ? 'border-white/5 bg-slate-900/30'
                    : 'border-slate-100 bg-slate-50/50'
                    }`}>
                    <input
                        ref={pdfInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={handlePdfUpload}
                        className="hidden"
                    />
                    <button
                        onClick={() => pdfInputRef.current?.click()}
                        disabled={isExtractingPdf || isParsing}
                        className={`mr-auto px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50 ${isDarkMode
                            ? 'bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 border border-emerald-500/30'
                            : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                            }`}
                        title="Extract text from an existing PDF resume"
                    >
                        {isExtractingPdf ? <Loader2 size={16} className="animate-spin" /> : <FileUp size={16} />}
                        {isExtractingPdf ? 'Extracting...' : 'Upload PDF'}
                    </button>
                    <button onClick={() => setStep('onboarding')} className={`px-4 py-2 rounded-lg transition-colors ${isDarkMode
                        ? 'text-slate-400 hover:text-white hover:bg-white/5'
                        : 'text-slate-600 hover:bg-slate-100'
                        }`}>Cancel</button>
                    <button
                        onClick={handleTextPasteParse}
                        className={`px-6 py-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg ${isDarkMode
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-blue-500/20'
                            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/20'
                            }`}
                        disabled={!pasteText.trim() || isParsing}
                    >
                        {isParsing ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                        {isParsing ? 'Smart Parsing...' : 'Parse Content'}
                    </button>
                </div>
            </div>
        </BackgroundLayout>
    );
};

export default PasteMode;
